
export default function Automation() {
  const features = [
    {
      icon: 'ri-mail-send-line',
      title: 'Automated Lead Nurturing',
      description: 'Every inquiry receives immediate, personalized follow-up via email, SMS, and calls—automatically.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Smart Appointment Booking',
      description: 'Patients can book consultations 24/7 with automated reminders and confirmations.'
    },
    {
      icon: 'ri-database-2-line',
      title: 'Healthcare CRM',
      description: 'Track every lead, contact, and patient through your entire pipeline with full visibility.'
    },
    {
      icon: 'ri-chat-4-line',
      title: 'Multi-Channel Communication',
      description: 'Automated email sequences, SMS campaigns, and voice calls that nurture leads to booking.'
    },
    {
      icon: 'ri-user-follow-line',
      title: 'Patient Intake Workflows',
      description: 'Streamline onboarding with automated forms, document collection, and pre-visit preparation.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Pipeline Tracking',
      description: 'Real-time visibility into every lead stage with automated task assignments and follow-ups.'
    }
  ];

  return (
    <section id="automation" className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] to-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              MARKETING AUTOMATION PLATFORM
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Never Miss a Lead Again
            </h2>
            <p className="text-base text-gray-600 leading-relaxed mb-8">
              Our proprietary marketing automation platform ensures every lead, contact, and patient is automatically followed up and nurtured—designed specifically for busy medical practices with HIPAA-conscious implementation.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-[#7C3AED] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">HIPAA-Aligned Implementation</h4>
                  <p className="text-sm text-gray-600">Secure systems that protect patient data while automating your marketing.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-[#7C3AED] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Save 20+ Hours Per Week</h4>
                  <p className="text-sm text-gray-600">Eliminate manual follow-up and focus on patient care while we handle lead nurturing.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-percent-line text-[#7C3AED] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">3x Higher Conversion Rates</h4>
                  <p className="text-sm text-gray-600">Automated follow-up converts more inquiries into booked appointments.</p>
                </div>
              </div>
            </div>

            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:from-[#6D28D9] hover:to-[#9333EA] transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>Request a Platform Demo</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          <div className="relative">
            <div className="w-full h-[700px] rounded-2xl overflow-hidden shadow-2xl bg-black">
              <iframe
                src="https://www.instagram.com/p/DRsZPA3CmNy/embed"
                className="w-full h-full border-0"
                allowFullScreen
                scrolling="no"
                title="MillionDollarMed Marketing Automation Demo"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">Lead Captured</div>
                  <div className="text-xs text-gray-500">Auto follow-up sent</div>
                </div>
              </div>
            </div>
            <div className="absolute top-4 -left-4 bg-white rounded-xl p-3 shadow-xl">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#7C3AED]/10 rounded-full flex items-center justify-center">
                  <i className="ri-mail-check-line text-[#7C3AED] text-sm"></i>
                </div>
                <div className="text-xs font-medium text-gray-700">247 Emails Sent Today</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-20">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer"
            >
              <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                <i className={`${feature.icon} text-[#7C3AED] text-xl`}></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-sm">{feature.title}</h4>
              <p className="text-xs text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
